import React, { useId } from 'react'

interface PlanStatusCardProps {
  title: string
  icon?: React.ReactNode
  planLabel?: string
  planValue?: string
  statusLabel?: string
  statusValue?: string
  size?: 'sm' | 'md' | 'lg'
}

export function PlanStatusCard({
  title,
  icon,
  planLabel = 'Plan',
  planValue = 'Invest €1000/mo in ETFs',
  statusLabel = 'Status',
  statusValue = '3 months ahead',
  size = 'md',
}: PlanStatusCardProps) {
  const uid = useId()
  const gradientId = `${uid}-chart-gradient`

  const sizeConfig = {
    sm: {
      width: 240,
      borderRadius: 8,
      padding: 12,
      iconSize: 14,
      titleSize: 'text-body-small',
      labelSize: 10,
      valueSize: 12,
      chartHeight: 60,
      svgHeight: 50
    },
    md: {
      width: 360,
      borderRadius: 10,
      padding: 16,
      iconSize: 18,
      titleSize: 'text-body-mobile',
      labelSize: 12,
      valueSize: 14,
      chartHeight: 100,
      svgHeight: 84
    },
    lg: {
      width: 420,
      borderRadius: 12,
      padding: 20,
      iconSize: 22,
      titleSize: 'text-body',
      labelSize: 14,
      valueSize: 16,
      chartHeight: 120,
      svgHeight: 100
    }
  }

  const config = sizeConfig[size]

  const HouseIcon = (
    <svg width={config.iconSize} height={config.iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 10.5L12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-10.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )

  return (
    <div
      style={{
        width: config.width,
        borderRadius: config.borderRadius,
        background: 'rgba(250, 249, 245, 0.3)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        padding: config.padding,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ color: 'var(--night)', display: 'grid', placeItems: 'center' }}>
          {icon !== undefined ? icon : HouseIcon}
        </div>
        <h4 className={config.titleSize} style={{ margin: 0, textAlign: 'center' }}>{title}</h4>
      </div>

      {/* Chart */}
      <div style={{ width: '100%', height: config.chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="100%" height={config.svgHeight} viewBox="0 0 248 84" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id={gradientId} x1="50.1931" y1="56.9605" x2="50.1931" y2="71.4027" gradientUnits="userSpaceOnUse">
              <stop stopColor="#C5ECAC"/>
              <stop offset="1" stopColor="#FAF9F5"/>
            </linearGradient>
          </defs>
          {/* Green area */}
          <path d="M102.639 46.3012L109 42L109 63H1C33.5372 61.6291 85.6344 56.2215 102.639 46.3012Z" fill={`url(#${gradientId})`} />
          {/* Strokes */}
          <path d="M1.15167 62.2564C1.15167 62.2564 85.3776 57.0583 136.708 44.4998C179.847 33.9456 243.208 0.999951 243.208 0.999951" stroke="#5D97D1" strokeWidth="0.5"/>
          <path d="M2.20825 62.5C2.20825 62.5 102.878 62.0584 154.208 49.5C197.347 38.9458 243.708 18 243.708 18" stroke="#5D97D1" strokeWidth="0.5"/>
        </svg>
      </div>

      {/* Footer */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 10 }}>
        <div>
          <div style={{ fontSize: config.labelSize, opacity: 0.7, marginBottom: 6 }}>{planLabel} ›</div>
          <div
            style={{
              background: 'rgba(168, 225, 223, 0.25)',
              borderRadius: 10,
              padding: '12px 14px',
              fontSize: config.valueSize,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            {planValue}
          </div>
        </div>
        <div>
          <div style={{ fontSize: config.labelSize, opacity: 0.7, marginBottom: 6 }}>{statusLabel} ›</div>
          <div
            style={{
              background: 'rgba(168, 225, 223, 0.25)',
              borderRadius: 10,
              padding: '12px 14px',
              fontSize: config.valueSize,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            {statusValue}
          </div>
        </div>
      </div>
    </div>
  )
}
